import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="flex justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <select
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            aria-label="Select virtual tour"
            className="font-body text-sm bg-white text-[var(--vibe-color-text)] border border-[var(--vibe-color-border)] rounded pl-3 pr-10 py-2.5 appearance-none bg-no-repeat focus:outline-none focus:border-[var(--vibe-color-primary)]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '1rem 1rem',
            }}
          >
            {validTours.map((tour, i) => (
              <option key={i} value={i}>
                {tour.virtual_tour_title || `Tour ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
